import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { TooltipBox } from './Tooltip';

const SHOW_DELAY = 300;

// The nearest element (from `start` upwards) whose hover text should show: one with
// a non-empty `title`, a `data-tooltip`, or one currently being shown by us (its
// title is parked in `data-tooltip-title`). An empty `title=""` stops the search,
// matching the browser (used by <Tooltip> anchors).
function findTooltipAnchor(start) {
  let el = start instanceof Element ? start : start?.parentElement ?? null;
  while (el && el !== document.body) {
    if (el.hasAttribute('data-tooltip-title') || el.hasAttribute('data-tooltip')) return el;
    if (el.hasAttribute('title')) return el.getAttribute('title') ? el : null;
    el = el.parentElement;
  }
  return null;
}

// ─── GlobalTooltips ─────────────────────────────────────────────────────────
// Mounted once at the app root. Replaces the browser's native tooltip for every
// element with a `title` attribute (and elements with a `data-tooltip` attribute,
// e.g. icons whose `title` prop would render an SVG <title>) with the styled
// TooltipBox: same look as <Tooltip>, positioned to stay on screen.
//
// While an element is hovered/focused its `title` is moved to `data-tooltip-title`
// (stopping the native tooltip) and put back afterwards, so the DOM and
// accessibility are unchanged the rest of the time. `data-tooltip-variant` sets the
// box variant ('warning', 'error', ...).
export default function GlobalTooltips() {
  const id = useId();
  const [tip, setTip] = useState(null); // { anchor, text, variant }
  const currentRef = useRef(null);
  const timerRef = useRef(null);
  const observerRef = useRef(null);

  const textOf = (el) => el.getAttribute('data-tooltip-title') ?? el.getAttribute('data-tooltip') ?? '';

  // Move the native title aside so the browser doesn't show its own tooltip.
  const parkTitle = (el) => {
    const title = el.getAttribute('title');
    if (title) {
      el.setAttribute('data-tooltip-title', title);
      el.removeAttribute('title');
    }
  };

  const deactivate = useCallback(() => {
    clearTimeout(timerRef.current);
    observerRef.current?.disconnect();
    const el = currentRef.current;
    currentRef.current = null;
    if (el) {
      const parked = el.getAttribute('data-tooltip-title');
      if (parked !== null) {
        // React may have set a new title meanwhile; it wins over the parked one.
        if (!el.hasAttribute('title')) el.setAttribute('title', parked);
        el.removeAttribute('data-tooltip-title');
      }
      el.removeAttribute('aria-describedby');
    }
    setTip(null);
  }, []);

  useEffect(() => {
    const activate = (el) => {
      if (el === currentRef.current) return;
      deactivate();
      currentRef.current = el;
      parkTitle(el);
      // If React re-renders the title (e.g. "Copy" -> "Copied") while shown, park
      // the new one too and update the box.
      const observer = new MutationObserver(() => {
        if (el.hasAttribute('title')) {
          el.removeAttribute('data-tooltip-title');
          parkTitle(el);
        }
        setTip((prev) => (prev && prev.anchor === el ? { ...prev, text: textOf(el) } : prev));
      });
      observer.observe(el, { attributes: true, attributeFilter: ['title', 'data-tooltip'] });
      observerRef.current = observer;

      timerRef.current = setTimeout(() => {
        if (currentRef.current !== el) return;
        const text = textOf(el);
        if (!text) return;
        el.setAttribute('aria-describedby', id);
        setTip({ anchor: el, text, variant: el.getAttribute('data-tooltip-variant') || 'default' });
      }, SHOW_DELAY);
    };

    const onOver = (e) => {
      const el = findTooltipAnchor(e.target);
      if (el) activate(el);
      else if (currentRef.current) deactivate();
    };
    const onOut = (e) => {
      const el = currentRef.current;
      if (el && !(e.relatedTarget instanceof Node && el.contains(e.relatedTarget))) deactivate();
    };
    const onFocusIn = (e) => {
      const el = findTooltipAnchor(e.target);
      if (el) activate(el);
    };
    const onFocusOut = (e) => {
      const el = currentRef.current;
      if (el && el.contains(e.target)) deactivate();
    };
    // Like the native tooltip, hide on click or Escape.
    const onKeyDown = (e) => {
      if (e.key === 'Escape') deactivate();
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('mousedown', deactivate);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('mousedown', deactivate);
      document.removeEventListener('keydown', onKeyDown);
      deactivate();
    };
  }, [deactivate, id]);

  if (!tip) return null;
  return <TooltipBox anchor={tip.anchor} text={tip.text} variant={tip.variant} id={id} onAnchorGone={deactivate} />;
}
